import React, { ReactNode, useMemo } from 'react';
import {
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInWeeks,
  format,
  parseISO,
} from 'date-fns';
import { WorkflowItem, workflowItemNames } from '../../config';
import './kanban-card-body.scss';

// =======================================
// INTERFACES
// =======================================

interface KanbanCardBodyProps {
  item: WorkflowItem;
  /** The card's own controls (the Move to button), at the end of the meta row. */
  actions?: ReactNode;
  /**
   * Render the title as the card's open button (the card in its lane). Its
   * click bubbles to the card, which does the opening. Off for the drag
   * overlay, which is a picture of the card, not a control.
   */
  openable?: boolean;
}

interface Tally {
  done: number;
  total: number;
}

// =======================================
// HELPERS
// =======================================

const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
};

/** "now", "12m", "3h", "2d", "5w", then a date: a card has room for one word. */
const shortAgo = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const minutes = differenceInMinutes(now, date);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = differenceInHours(now, date);
  if (hours < 24) return `${hours}h`;
  const days = differenceInDays(now, date);
  if (days < 7) return `${days}d`;
  const weeks = differenceInWeeks(now, date);
  if (weeks < 5) return `${weeks}w`;
  return format(date, 'd MMM');
};

const regionSlug = (region: string): string => region.toLowerCase().replace(/ /g, '-');

const daysWord = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`;

interface RwcAge {
  text: string;
  /** The date the count runs from, for the tooltip. */
  title?: string;
}

/**
 * How long an RWC job has sat at the point its column cares about:
 * - Probable: since the job was created
 * - Active, Inspections Invoice: since the last inspection
 * - QA Review: since the last inspection, which is when QA starts
 * - Final Invoice: since the job was moved into the column
 * Calendar days in local time; a date-only inspection date is read as a
 * local date (parseISO), not UTC midnight.
 */
const rwcAge = (item: WorkflowItem): RwcAge | null => {
  // An item with no status sits in the first column, Probable on every board.
  const status = item.workflow_status ?? 'Probable';
  const since = (iso: string | null) => {
    if (!iso) return null;
    const date = parseISO(iso);
    if (Number.isNaN(date.getTime())) return null;
    return { days: Math.max(0, differenceInCalendarDays(new Date(), date)), label: format(date, 'd MMM yyyy') };
  };

  switch (status) {
    case 'Probable': {
      const s = since(item.created_at);
      if (!s) return null;
      return {
        text: s.days === 0 ? 'Created today' : `${daysWord(s.days)} since created`,
        title: `Created ${s.label}`,
      };
    }
    case 'Active':
    case 'Inspections Invoice': {
      const s = since(item.last_inspection_date);
      if (!s) return { text: 'No inspections yet' };
      return {
        text: s.days === 0 ? 'Inspected today' : `${daysWord(s.days)} since last inspection`,
        title: `Last inspection ${s.label}`,
      };
    }
    case 'QA Review': {
      const s = since(item.last_inspection_date);
      if (!s) return { text: 'No inspections yet' };
      return {
        text: s.days === 0 ? 'QA started today' : `${daysWord(s.days)} in QA`,
        title: `Counted from the last inspection, ${s.label}`,
      };
    }
    case 'Final Invoice': {
      const s = since(item.workflow_status_changed_at);
      if (!s) return null;
      return {
        text: s.days === 0 ? 'Moved to Final Invoice today' : `${daysWord(s.days)} in Final Invoice`,
        title: `Moved to Final Invoice ${s.label}`,
      };
    }
    default:
      return null;
  }
};

// =======================================
// ICONS
// =======================================

const BuildingIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21M19 21H5M19 21H21M5 21H3M9 7H10M9 11H10M14 7H15M14 11H15M9 21V16C9 15.4477 9.44772 15 10 15H14C14.5523 15 15 15.4477 15 16V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MessageIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// =======================================
// PROGRESS ROW
// =======================================

interface ProgressRowProps {
  label: string;
  tally: Tally;
  /** The pre-start (startup) meeting, as a dot before the bar: null when none is held. */
  startup?: boolean | null;
  /** Leave the dot's room empty, so this bar lines up under one that has it. */
  indent?: boolean;
}

/** One line of RWC progress: the label, then the bar with its count at the right. */
const ProgressRow: React.FC<ProgressRowProps> = ({ label, tally, startup = null, indent = false }) => {
  const startupText = startup === null ? '' : `Pre-start meeting ${startup ? 'done' : 'to do'}`;
  return (
    <div
      className="kcard__progress-row"
      aria-label={[`${label} ${tally.done} of ${tally.total}`, startupText].filter(Boolean).join(', ')}
    >
      <span className="kcard__progress-bar" aria-hidden="true">
        {indent && startup === null && <span className="kcard__startup is-spacer" />}
        {startup !== null && (
          <span
            className={`kcard__startup ${startup ? 'is-done' : ''}`.trim()}
            title={startupText}
          />
        )}
        {Array.from({ length: tally.total }, (_, i) => (
          <span key={i} className={`kcard__segment ${i < tally.done ? 'is-done' : ''}`.trim()} />
        ))}
      </span>
      <span className="kcard__progress-count" aria-hidden="true">
        {tally.total > 0 ? `${tally.done}/${tally.total}` : ''}
      </span>
      <span className="kcard__progress-label" aria-hidden="true">{label}</span>
    </div>
  );
};

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * What a board card shows, with no drag wiring: the card in the column and
 * the lifted copy under the pointer while dragging render the same body, so
 * the card never changes shape as it is picked up.
 */
const KanbanCardBody: React.FC<KanbanCardBodyProps> = ({ item, actions, openable = false }) => {
  const isRwc = item.types === 'RWC';
  const isStage = item.entity_type === 'stage';
  const { title, stageLine } = workflowItemNames(item);

  // RWC progress, one bar each: inspections (with the pre-start meeting as a
  // dot in front, when one is held) and QA items.
  const rwc = useMemo(() => {
    if (!isRwc) return null;
    const tally = (done: number | null, total: number | null): Tally => {
      const t = total || 0;
      return { done: Math.min(done || 0, t), total: t };
    };
    const startup = item.startup_meeting ? !!item.start_up_completion_date : null;
    const inspections = tally(item.inspection_count, item.number_of_inspections);
    const qa = tally(item.qa_completed_count, item.qa_required_count);
    if (startup === null && inspections.total === 0 && qa.total === 0) return null;
    return { startup, inspections, qa };
  }, [isRwc, item]);

  const age = isRwc ? rwcAge(item) : null;

  const tags = isRwc ? item.tags?.slice(0, 2) ?? [] : [];
  const lastMessage = item.last_message;

  return (
    <>
      <div className="kcard__top">
        <span className="kcard__number">{item.job_number}</span>
        {isStage && <span className="ui-chip kcard__stage">Stage</span>}
        {(isRwc && (item.region || tags.length > 0)) && (
          <span className="kcard__chips">
            {item.region && (
              <span className={`ui-chip region-chip region-chip--${regionSlug(item.region)}`}>
                {item.region}
              </span>
            )}
            {tags.map(tag => (
              <span key={tag.id} className="ui-chip">{tag.name}</span>
            ))}
          </span>
        )}
      </div>

      {openable ? (
        // A stage's button is named with its stage too: every stage of one
        // project shares the title, and the buttons must be told apart.
        <button
          type="button"
          className="kcard__open"
          title={title}
          aria-label={stageLine ? `${title}, ${stageLine}` : undefined}
        >
          <span className="kcard__title">{title}</span>
        </button>
      ) : (
        <div className="kcard__title" title={title}>{title}</div>
      )}
      {stageLine && (
        <div className="kcard__stage-name" title={stageLine}>{stageLine}</div>
      )}

      {rwc && (
        <div className="kcard__progress">
          {(rwc.inspections.total > 0 || rwc.startup !== null) && (
            <ProgressRow
              // Worded as the invoice's "Pre-start + Inspections" claim.
              label={rwc.startup !== null ? 'Pre-start + Inspections' : 'Inspections'}
              tally={rwc.inspections}
              startup={rwc.startup}
            />
          )}
          {rwc.qa.total > 0 && (
            <ProgressRow label="QA" tally={rwc.qa} indent={rwc.startup !== null} />
          )}
        </div>
      )}

      {age && (
        <div className="kcard__age" title={age.title}>
          <ClockIcon />
          <span>{age.text}</span>
        </div>
      )}

      <div className="kcard__meta">
        <span className="kcard__client" title={item.client?.company_name}>
          {item.client && (
            <>
              <BuildingIcon />
              <span>{item.client.company_name}</span>
            </>
          )}
        </span>
        {item.project_lead && (
          <span
            className="ui-avatar ui-avatar--lead"
            title={item.project_lead.full_name}
            aria-label={`Lead: ${item.project_lead.full_name}`}
          >
            {getInitials(item.project_lead.first_name, item.project_lead.last_name)}
          </span>
        )}
        {actions}
      </div>

      {lastMessage && (
        <div
          className="kcard__activity"
          title={`${lastMessage.sender_first_name}: ${lastMessage.message}`}
        >
          <MessageIcon />
          <p>
            <span className="kcard__sender">{lastMessage.sender_first_name}</span>
            <span className="kcard__when"> · {shortAgo(lastMessage.created_at)}</span>{' '}
            {lastMessage.message}
          </p>
        </div>
      )}
    </>
  );
};

export default KanbanCardBody;
